import type { Step } from 'react-joyride';

// How long joyride waits for a step's target to appear in the DOM before
// showing that step's tooltip. The client view re-fetches the profile with the
// viewer's auth to upgrade tier-gated contact fields, so wait generously to
// avoid the tour silently skipping a step whose element isn't ready yet.
const TARGET_WAIT_TIMEOUT_MS = 3_000;

/**
 * Tour steps for an advocate profile (`/advokati/[id]`) — the screen a client
 * lands on from a directory card, and the last stop before contacting someone.
 *
 * Only the hero is unconditional. The contact card renders when the record has
 * any contact detail, and the registry card when it has a `registry_source`;
 * `ProfileTour` appends each only when it is actually on the page, so a sparse
 * profile shows a shorter tour rather than a step pointing at nothing. The
 * hero-only case is filtered out entirely by the tour — one step over an
 * avatar teaches nothing.
 *
 * Copy is resolved from the `Tour.profile.*` i18n namespace at render time —
 * these are keys, not hardcoded strings.
 */
export const profileTourSteps: Step[] = [
  {
    target: '[data-tour="profile-hero"]',
    title: 'profile.title.hero',
    content: 'profile.content.hero',
    placement: 'bottom' as const,
    skipBeacon: true,
    targetWaitTimeout: TARGET_WAIT_TIMEOUT_MS,
  },
];

/**
 * The conditional half — see `profileTourSteps` for why these are separate.
 * Order matters: contact before registry, so the reveal mechanic is explained
 * before the provenance of the data being revealed.
 */
export const profileOptionalSteps: Step[] = [
  {
    target: '[data-tour="profile-contact"]',
    title: 'profile.title.contact',
    content: 'profile.content.contact',
    placement: 'left' as const,
    skipBeacon: true,
    targetWaitTimeout: TARGET_WAIT_TIMEOUT_MS,
  },
  {
    target: '[data-tour="profile-registry"]',
    title: 'profile.title.registry',
    content: 'profile.content.registry',
    placement: 'left' as const,
    skipBeacon: true,
    targetWaitTimeout: TARGET_WAIT_TIMEOUT_MS,
  },
];
